import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, CircleNotch, FilePdf, LinkSimple, PaperPlaneTilt, CheckCircle, Receipt, BellRinging, ClockCounterClockwise } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const isOverdue = (inv) => inv.status !== "paid" && inv.due_date && inv.due_date < today();
const STATUS = {
  draft: { label: "Draft", bg: "var(--bk-page)", color: "var(--bk-text-2)" },
  sent: { label: "Sent", bg: "#EAF0F6", color: "#3A5A7A" },
  paid: { label: "Paid", bg: "var(--bk-success-bg)", color: "#2F6B4F" },
};

export function Invoices({ businessId }) {
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState({ enabled: true });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [reminding, setReminding] = useState("");
  const [togglingBusiness, setTogglingBusiness] = useState(false);
  const [togglingInvoice, setTogglingInvoice] = useState("");
  const [form, setForm] = useState({ client_name: "", client_email: "", issue_date: today(), due_date: "", tax_rate: "", notes: "" });
  const [items, setItems] = useState([{ description: "", quantity: 1, rate: "" }]);
  const [jobs, setJobs] = useState([]);
  const [formJob, setFormJob] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);

  const load = () => api.get(`/businesses/${businessId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
  const loadSettings = () => api.get(`/businesses/${businessId}/invoice-reminders/settings`).then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadSettings();
    api.get(`/businesses/${businessId}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [businessId]);

  const subtotal = items.reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
  const discountAmt = discount ? subtotal * (Number(discount.rate) || 0) / 100 : 0;
  const taxAmt = (subtotal - discountAmt) * (Number(form.tax_rate) || 0) / 100;

  const applyCode = async () => {
    const code = discountCode.trim();
    if (!code) { setDiscount(null); return; }
    setCheckingCode(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/validate-discount`, { code });
      if (data.valid) { setDiscount(data); toast.success(`Applied ${data.label}`); }
      else { setDiscount(null); toast.error("That code isn't a valid member discount for this business."); }
    } catch { toast.error("Couldn't check that code."); }
    finally { setCheckingCode(false); }
  };

  const setItem = (i, k, v) => setItems((xs) => xs.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((xs) => [...xs, { description: "", quantity: 1, rate: "" }]);
  const removeItem = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));

  const create = async () => {
    if (!form.client_name.trim()) return toast.error("Add a client name");
    const clean = items.filter((it) => it.description.trim() && Number(it.rate) > 0);
    if (clean.length === 0) return toast.error("Add at least one line item with a rate");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/invoices`, {
        client_name: form.client_name, client_email: form.client_email, issue_date: form.issue_date,
        due_date: form.due_date, tax_rate: Number(form.tax_rate) || 0, notes: form.notes,
        items: clean.map((it) => ({ description: it.description, quantity: Number(it.quantity) || 1, rate: Number(it.rate) || 0 })),
        job_id: formJob || null,
        discount_code: discount ? discount.code : "",
      });
      toast.success("Invoice created");
      setForm({ client_name: "", client_email: "", issue_date: today(), due_date: "", tax_rate: "", notes: "" });
      setItems([{ description: "", quantity: 1, rate: "" }]);
      setFormJob("");
      setDiscountCode(""); setDiscount(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create invoice.");
    } finally { setSaving(false); }
  };

  const patch = async (inv, body) => {
    try {
      const { data } = await api.patch(`/invoices/${inv.id}`, body);
      setInvoices((xs) => xs.map((x) => (x.id === inv.id ? data : x)));
      if (body.status === "paid") toast.success("Marked paid — income added to your Books");
    } catch { toast.error("Couldn't update invoice."); }
  };

  const del = async (id) => {
    try { await api.delete(`/invoices/${id}`); setInvoices((x) => x.filter((i) => i.id !== id)); } catch {}
  };

  const copyLink = (inv) => {
    const link = `${window.location.origin}/i/${inv.public_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Invoice link copied");
    if (inv.status === "draft") patch(inv, { status: "sent" });
  };

  const sendReminder = async (inv) => {
    setReminding(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/reminder`, { origin_url: window.location.origin });
      await navigator.clipboard.writeText(data.message);
      toast.success("Witty reminder copied — paste it to your client", { description: data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't write that reminder.");
    } finally { setReminding(""); }
  };

  const downloadPdf = async (inv) => {
    setBusy(inv.id);
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't generate the PDF."); }
    finally { setBusy(""); }
  };

  const toggleBusiness = async () => {
    setTogglingBusiness(true);
    try {
      const { data } = await api.patch(`/businesses/${businessId}/invoice-reminders/settings`, { enabled: !settings.enabled });
      setSettings(data);
      toast.success(data.enabled ? "Automated reminders enabled" : "Automated reminders paused");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update reminder settings.");
    } finally {
      setTogglingBusiness(false);
    }
  };

  const toggleInvoice = async (inv) => {
    setTogglingInvoice(inv.id);
    try {
      const { data } = await api.patch(`/invoices/${inv.id}`, { automated_reminders_enabled: !(inv.automated_reminders_enabled !== false) });
      setInvoices((xs) => xs.map((x) => (x.id === inv.id ? data : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update invoice reminder settings.");
    } finally {
      setTogglingInvoice("");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-panel">
      {invoices.length > 0 && (() => {
        const paid = invoices.filter((i) => i.status === "paid").reduce((a, i) => a + (i.total || 0), 0);
        const outstanding = invoices.filter((i) => i.status !== "paid").reduce((a, i) => a + (i.total || 0), 0);
        const overdue = invoices.filter((i) => isOverdue(i)).reduce((a, i) => a + (i.total || 0), 0);
        const overdueCount = invoices.filter((i) => isOverdue(i)).length;
        const cards = [
          { key: "paid", label: "Paid", value: paid, color: "var(--bk-success)", testid: "summary-paid" },
          { key: "outstanding", label: "Outstanding", term: "outstanding", value: outstanding, color: "var(--bk-accent)", testid: "summary-outstanding" },
          { key: "overdue", label: overdueCount ? `Overdue (${overdueCount})` : "Overdue", term: "overdue", value: overdue, color: "#B23B1F", testid: "summary-overdue" },
        ];
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="payments-summary">
            {cards.map((c) => (
              <div key={c.key} className="bk-card p-5 bk-lift" data-testid={c.testid}>
                <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>{c.term ? <RowanTerm term={c.term} size={12}>{c.label}</RowanTerm> : c.label}</span>
                <p className="font-display text-2xl font-bold mt-1.5" style={{ color: c.color }}>{money(c.value)}</p>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="bk-card p-6 flex items-center justify-between bk-lift">
        <div>
          <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Invoices</h3>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Create a professional invoice, share a link or PDF, and record it as income when paid.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center justify-end">
          <button onClick={toggleBusiness} disabled={togglingBusiness} data-testid="invoice-reminder-business-toggle" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border disabled:opacity-60" style={{ borderColor: settings.enabled ? "var(--bk-success)" : "var(--bk-border)", color: settings.enabled ? "var(--bk-success)" : "var(--bk-text)" }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: settings.enabled ? "var(--bk-success)" : "#B0B7C3" }} />
            {settings.enabled ? "Automated reminders on" : "Automated reminders off"}
          </button>
          <button onClick={() => setShowForm((s) => !s)} data-testid="new-invoice-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold shrink-0">
            <Plus size={16} weight="bold" /> New invoice
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bk-card p-6 space-y-4" data-testid="invoice-form" style={{ background: "linear-gradient(180deg, rgba(255,249,240,0.55), #fff)" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Client name" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="inv-client-name" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Client email (optional)" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="inv-client-email" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Issue date<DateInput value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} data-testid="inv-issue-date" className="rounded-md border px-3 py-2 text-sm outline-none w-full mt-1" style={{ borderColor: "var(--bk-border)" }} /></label>
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Due date<DateInput value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="inv-due-date" className="rounded-md border px-3 py-2 text-sm outline-none w-full mt-1" style={{ borderColor: "var(--bk-border)" }} /></label>
          </div>

          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`inv-item-${i}`}>
                <input placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`inv-item-desc-${i}`} className="flex-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`inv-item-qty-${i}`} className="w-16 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} data-testid={`inv-item-rate-${i}`} className="w-24 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <span className="w-24 text-right text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{money((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</span>
                <button onClick={() => removeItem(i)} className="p-1.5 rounded hover:bg-black/5" style={{ color: "#B23B1F" }} aria-label="Remove"><Trash size={15} weight="bold" /></button>
              </div>
            ))}
            <button onClick={addItem} data-testid="inv-add-item" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-accent)" }}><Plus size={14} weight="bold" /> Add line</button>
          </div>

          <div className="flex flex-wrap items-end gap-4 justify-between">
            <div className="flex flex-wrap items-end gap-4">
              <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Tax rate (%)<input type="number" placeholder="0" value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} data-testid="inv-tax-rate" className="rounded-md border px-3 py-2 text-sm outline-none w-24 mt-1 block" style={{ borderColor: "var(--bk-border)" }} /></label>
              <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Member discount code
                <div className="flex items-center gap-2 mt-1">
                  <input placeholder="e.g. MEMBER15" value={discountCode} onChange={(e) => { setDiscountCode(e.target.value); setDiscount(null); }} data-testid="inv-discount-code" className="rounded-md border px-3 py-2 text-sm outline-none w-36 block uppercase" style={{ borderColor: discount ? "var(--bk-success)" : "var(--bk-border)" }} />
                  <button onClick={applyCode} disabled={checkingCode} data-testid="inv-apply-discount" className="bk-btn-secondary text-sm font-semibold px-3 py-2 disabled:opacity-60" style={{ color: "var(--bk-accent)" }}>
                    {checkingCode ? <CircleNotch size={14} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
              </label>
            </div>
            <div className="text-right text-sm" style={{ color: "var(--bk-text)" }}>
              <div>Subtotal: <span className="font-semibold">{money(subtotal)}</span></div>
              {discount && <div data-testid="inv-discount-line" style={{ color: "var(--bk-success)" }}>Discount ({discount.code}): <span className="font-semibold">-{money(discountAmt)}</span></div>}
              <div>Tax: <span className="font-semibold">{money(taxAmt)}</span></div>
              <div className="font-display text-lg font-bold" data-testid="inv-total-preview">Total: {money(subtotal - discountAmt + taxAmt)}</div>
            </div>
          </div>
          <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="inv-notes" className="w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          {jobs.length > 0 && (
            <select value={formJob} onChange={(e) => setFormJob(e.target.value)} data-testid="inv-job" className="w-full rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              <option value="">Link to a job (optional)</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>Job: {j.name}</option>)}
            </select>
          )}
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="save-invoice-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Creating…</> : <>Create invoice</>}
            </button>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="bk-empty-state p-10 text-center">
          <Receipt size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>Nothing here yet. Let&apos;s change that.</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Create your first invoice to bill a client.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => {
            const st = STATUS[inv.status] || STATUS.draft;
            return (
              <div key={inv.id} data-testid={`invoice-${inv.id}`} className="bk-card p-5 bk-lift">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{inv.number}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }} data-testid={`invoice-status-${inv.id}`}>{st.label}</span>
                      {isOverdue(inv) && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#FDECE8", color: "#B23B1F" }} data-testid={`invoice-overdue-${inv.id}`}>Overdue</span>}
                    </div>
                    <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>{inv.client_name} · {inv.due_date ? `due ${inv.due_date}` : "no due date"}</p>
                  </div>
                  <span className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{money(inv.total)}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => downloadPdf(inv)} disabled={busy === inv.id} data-testid={`invoice-pdf-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md border hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    {busy === inv.id ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />} PDF
                  </button>
                  <button onClick={() => copyLink(inv)} data-testid={`invoice-copylink-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md border hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <LinkSimple size={14} weight="bold" /> Copy link
                  </button>
                  {inv.status !== "sent" && inv.status !== "paid" && (
                    <button onClick={() => patch(inv, { status: "sent" })} data-testid={`invoice-mark-sent-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md border hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                      <PaperPlaneTilt size={14} weight="bold" /> Mark sent
                    </button>
                  )}
                  {isOverdue(inv) && (
                    <button onClick={() => sendReminder(inv)} disabled={reminding === inv.id} data-testid={`invoice-remind-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md" style={{ background: "#FDECE8", color: "#B23B1F" }}>
                      {reminding === inv.id ? <CircleNotch size={14} className="animate-spin" /> : <BellRinging size={14} weight="bold" />} Send reminder
                    </button>
                  )}
                  {inv.status !== "paid" && (
                    <button onClick={() => patch(inv, { status: "paid" })} data-testid={`invoice-mark-paid-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md" style={{ background: "var(--bk-success-bg)", color: "#2F6B4F" }}>
                      <CheckCircle size={14} weight="bold" /> Mark paid
                    </button>
                  )}
                  <button onClick={() => toggleInvoice(inv)} disabled={togglingInvoice === inv.id} data-testid={`invoice-auto-toggle-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: inv.automated_reminders_enabled !== false ? "var(--bk-success)" : "var(--bk-text-2)" }}>
                    {togglingInvoice === inv.id ? <CircleNotch size={14} className="animate-spin" /> : <BellRinging size={14} weight="bold" />} {inv.automated_reminders_enabled !== false ? "Auto reminders on" : "Auto reminders off"}
                  </button>
                  <button onClick={() => del(inv.id)} data-testid={`invoice-delete-${inv.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md ml-auto" style={{ color: "#B23B1F" }}>
                    <Trash size={14} weight="bold" /> Delete
                  </button>
                </div>
                {inv.reminder_history?.length > 0 && (
                  <div className="mt-4 rounded-2xl p-4" style={{ background: "var(--bk-page)" }} data-testid={`invoice-reminder-history-${inv.id}`}>
                    <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Reminder history</p>
                    <div className="space-y-2 mt-3">
                      {inv.reminder_history.slice().reverse().map((item, idx) => (
                        <div key={`${item.sent_at}-${idx}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="inline-flex items-center gap-2" style={{ color: "var(--bk-text)" }}><ClockCounterClockwise size={14} weight="duotone" color="#FF6B35" /> {item.subject || item.stage?.replace("_", " ")}</span>
                          <span style={{ color: "var(--bk-text-2)" }}>{new Date(item.sent_at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    {inv.paid_after_reminder && <p className="text-xs mt-3 font-semibold" style={{ color: "var(--bk-success)" }}>Paid after reminder</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
